import { useState, useMemo, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { format, addDays, startOfToday, isSameDay } from 'date-fns';
import { es, enUS } from 'date-fns/locale';
import { TopBar } from '../components/layout/TopBar';
import { useAuthStore, useAppStore } from '../store/useStore';
import api from '../lib/api';
import ReservationWidget from '../components/widgets/ReservationWidget';
import WidgetIntegrationPanel from '../components/widgets/WidgetIntegrationPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  Code2, 
  Copy, 
  ExternalLink,
  Palette,
  Layout,
  Eye,
  Users,
  Calendar,
  Clock,
  ChevronLeft,
  ChevronRight,
  Check,
  Upload,
  Image,
  Sparkles,
  Minimize2,
  Zap,
  Share2,
  MessageSquare,
  Mic,
  Facebook,
  Instagram,
  Twitter,
  Link2,
  BookOpen,
  HelpCircle,
  Bot,
  Wand2,
  PartyPopper,
  Ticket,
  Save,
  Loader2
} from 'lucide-react';

export default function WidgetBuilder() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const { theme } = useAppStore();
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const fileInputRef = useRef(null);

  // Theme presets
  const themePresets = {
    elegant: {
      name: 'Elegante',
      description: 'Sofisticado y profesional',
      icon: Sparkles,
      primaryColor: '#1a1a1a',
      accentColor: '#C9A962',
      borderRadius: '16px',
      fontStyle: 'serif',
      theme: 'dark'
    },
    minimal: {
      name: 'Minimalista',
      description: 'Limpio y moderno',
      icon: Minimize2,
      primaryColor: '#F97316',
      accentColor: '#ffffff',
      borderRadius: '12px',
      fontStyle: 'sans',
      theme: 'light'
    },
    innovative: {
      name: 'Innovador',
      description: 'Dinámico y colorido',
      icon: Zap,
      primaryColor: '#8B5CF6',
      accentColor: '#06B6D4',
      borderRadius: '24px',
      fontStyle: 'sans',
      theme: 'dark'
    }
  };

  const [activeTab, setActiveTab] = useState('design');
  const [selectedPreset, setSelectedPreset] = useState('minimal');
  const [restaurantLogo, setRestaurantLogo] = useState(null);
  const [uploadingLogo, setUploadingLogo] = useState(false);

  const [config, setConfig] = useState({
    theme: 'dark',
    primaryColor: '#F97316',
    buttonStyle: 'modal',
    language: 'es',
    showLogo: true,
    restaurantName: 'Mi Restaurante',
    restaurantLogo: null,
    preset: 'minimal',
    borderRadius: '12px',
  });

  // Preview state
  const [previewDate, setPreviewDate] = useState(startOfToday());
  const [previewTime, setPreviewTime] = useState('20:00');
  const [previewGuests, setPreviewGuests] = useState(2);
  const [previewStep, setPreviewStep] = useState(1); // 1: date/time, 2: details, 3: confirm

  const restaurantId = user?.restaurant_id || 'YOUR_RESTAURANT_ID';
  const [saving, setSaving] = useState(false);

  // Load config from localStorage on mount
  useEffect(() => {
    if (!restaurantId || restaurantId === 'YOUR_RESTAURANT_ID') return;
    try {
      const saved = localStorage.getItem(`widget_res_config_${restaurantId}`);
      if (saved) setConfig(prev => ({ ...prev, ...JSON.parse(saved) }));
    } catch {}
  }, [restaurantId]);

  const saveConfig = () => {
    setSaving(true);
    try {
      localStorage.setItem(`widget_res_config_${restaurantId}`, JSON.stringify(config));
      toast.success('Configuración guardada');
    } catch { toast.error('Error al guardar'); }
    finally { setTimeout(() => setSaving(false), 800); }
  };

  // Handle logo upload
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Por favor selecciona una imagen válida');
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen no debe superar 2MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await api.post('/restaurants/upload-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setRestaurantLogo(response.data.url);
      setConfig(prev => ({ ...prev, restaurantLogo: response.data.url }));
      toast.success('Logo subido correctamente');
    } catch (error) {
      // For now, use local preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setRestaurantLogo(e.target.result);
        setConfig(prev => ({ ...prev, restaurantLogo: e.target.result }));
      };
      reader.readAsDataURL(file);
      toast.success('Logo cargado (vista previa)');
    } finally {
      setUploadingLogo(false);
    }
  };

  // Apply preset theme
  const applyPreset = (presetKey) => {
    const preset = themePresets[presetKey];
    setSelectedPreset(presetKey);
    setConfig(prev => ({
      ...prev,
      primaryColor: preset.primaryColor,
      theme: preset.theme,
      borderRadius: preset.borderRadius,
      preset: presetKey
    }));
  };

  // Generate dates for horizontal calendar
  const calendarDates = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => addDays(startOfToday(), i));
  }, []);

  // Time slots
  const timeSlots = [
    '12:00', '12:30', '13:00', '13:30', '14:00', '14:30', '15:00',
    '19:00', '19:30', '20:00', '20:30', '21:00', '21:30', '22:00', '22:30'
  ];

  // URL canónica de PRODUCCIÓN para los snippets que el cliente copia y pega
  const PRODUCTION_URL = 'https://okreserva.okgo.mx';

  // Parámetros de URL que el widget público entiende (sólo lang y color)
  const widgetUrlParams = useMemo(() => ({
    lang: config.language || 'es',
    color: (config.primaryColor || '#F97316').replace('#', ''),
  }), [config.language, config.primaryColor]);

  // Config para el JS SDK (los campos que widget.js realmente lee)
  const widgetSdkConfig = useMemo(() => ({
    buttonStyle: config.buttonStyle,
    theme: config.theme,
    primaryColor: config.primaryColor,
    language: config.language,
    showLogo: config.showLogo,
    restaurantName: config.restaurantName || '',
  }), [config.buttonStyle, config.theme, config.primaryColor, config.language, config.showLogo, config.restaurantName]);

  // Estado para controlar qué widget se está previsualizando
  const [previewType, setPreviewType] = useState('reservations'); // 'reservations' | 'events'

  // Widget Preview Component - Now using the new 3-step ReservationWidget
  const WidgetPreview = () => {
    const isDark = config.theme === 'dark';
    
    return (
      <div className="max-w-md mx-auto">
        <ReservationWidget
          restaurantId={user?.restaurant_id}
          restaurantName={config.restaurantName || 'Mi Restaurante'}
          logoUrl={restaurantLogo}
          primaryColor={config.primaryColor}
          customOccasions={[]}
          enabledOccasions={['anniversary', 'romantic', 'birthday', 'friends', 'graduation', 'business', 'proposal', 'family', 'none']}
          cancellationPolicy={config.cancellationPolicy || ''}
          language={config.language || 'es'}
          onSuccess={() => toast.success('¡Reservación creada!')}
          onClose={() => {}}
          API_URL={BACKEND_URL}
        />
      </div>
    );
  };

  return (
    <div className="min-h-screen" data-testid="widget-page">
      <TopBar title="Widget de Reservaciones" actions={
        <Button onClick={saveConfig} disabled={saving} className="bg-orange-500 hover:bg-orange-600 text-white h-8">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Save className="h-3.5 w-3.5 mr-1" />} Guardar
        </Button>
      } />

      <div className="p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-4 mb-6">
            <TabsTrigger value="design" className="flex items-center gap-2">
              <Palette className="h-4 w-4" /> Diseño
            </TabsTrigger>
            <TabsTrigger value="code" className="flex items-center gap-2">
              <Code2 className="h-4 w-4" /> Integración
            </TabsTrigger>
            <TabsTrigger value="share" className="flex items-center gap-2">
              <Share2 className="h-4 w-4" /> Compartir
            </TabsTrigger>
            <TabsTrigger value="bot" className="flex items-center gap-2">
              <Bot className="h-4 w-4" /> Voice Bot
            </TabsTrigger>
          </TabsList>

          {/* DESIGN TAB */}
          <TabsContent value="design">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Configuration Panel */}
              <div className="space-y-6">
                {/* Theme Presets */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Wand2 className="h-5 w-5 text-primary" /> Temas Predefinidos
                    </CardTitle>
                    <CardDescription>Selecciona un estilo base</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-3 gap-3">
                      {Object.entries(themePresets).map(([key, preset]) => {
                        const Icon = preset.icon;
                        return (
                          <button
                            key={key}
                            onClick={() => applyPreset(key)}
                            className={cn(
                              "p-4 rounded-xl border-2 text-center transition-all",
                              selectedPreset === key
                                ? "border-primary bg-primary/5"
                                : "border-border hover:bg-muted"
                            )}
                          >
                            <div 
                              className="w-10 h-10 rounded-xl mx-auto mb-2 flex items-center justify-center"
                              style={{ backgroundColor: preset.primaryColor }}
                            >
                              <Icon className="h-5 w-5 text-white" />
                            </div>
                            <p className="font-semibold text-sm">{preset.name}</p>
                            <p className="text-xs text-muted-foreground mt-1">{preset.description}</p>
                          </button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>

                {/* Restaurant Logo */}
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Image className="h-5 w-5 text-primary" /> Logo del Restaurante
                    </CardTitle>
                    <CardDescription>Aparecerá en el header del widget</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center gap-4">
                      <div 
                        className={cn(
                          "w-24 h-24 rounded-xl border-2 border-dashed flex items-center justify-center cursor-pointer transition-all hover:bg-muted",
                          restaurantLogo ? "border-primary bg-primary/5" : "border-muted-foreground/30"
                        )}
                        onClick={() => fileInputRef.current?.click()}
                      >
                        {restaurantLogo ? (
                          <img src={restaurantLogo} alt="Logo" className="w-full h-full object-contain rounded-lg" />
                        ) : (
                          <Upload className="h-8 w-8 text-muted-foreground" />
                        )}
                      </div>
                      <div className="flex-1">
                        <Button 
                          variant="outline" 
                          onClick={() => fileInputRef.current?.click()}
                          disabled={uploadingLogo}
                          className="rounded-xl"
                        >
                          {uploadingLogo ? 'Subiendo...' : 'Subir Logo'}
                        </Button>
                        <p className="text-xs text-muted-foreground mt-2">
                          PNG o JPG, máximo 2MB
                        </p>
                      </div>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleLogoUpload}
                        className="hidden"
                      />
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg" data-testid="widget-config-card">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Palette className="h-5 w-5 text-primary" /> Personalización
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    <div>
                      <Label className="text-sm font-medium">Nombre del Restaurante</Label>
                      <Input
                        value={config.restaurantName}
                        onChange={(e) => setConfig({ ...config, restaurantName: e.target.value })}
                        placeholder="Mi Restaurante"
                        className="mt-1.5 h-11 rounded-xl"
                      />
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Tema</Label>
                      <Select 
                        value={config.theme} 
                        onValueChange={(value) => setConfig({ ...config, theme: value })}
                      >
                        <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dark">Oscuro</SelectItem>
                          <SelectItem value="light">Claro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Color Principal</Label>
                      <div className="flex gap-2 mt-1.5">
                        <div className="flex gap-1">
                          {['#F97316', '#EF4444', '#8B5CF6', '#06B6D4', '#10B981', '#C9A962'].map((color) => (
                            <button
                              key={color}
                              className={cn(
                                "w-9 h-9 rounded-xl transition-all",
                                config.primaryColor === color && "ring-2 ring-offset-2 ring-primary"
                              )}
                              style={{ backgroundColor: color }}
                              onClick={() => setConfig({ ...config, primaryColor: color })}
                            />
                          ))}
                        </div>
                        <Input
                          type="color"
                          value={config.primaryColor}
                          onChange={(e) => setConfig({ ...config, primaryColor: e.target.value })}
                          className="w-11 h-9 p-1 cursor-pointer rounded-xl"
                        />
                      </div>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Idioma</Label>
                      <Select 
                        value={config.language} 
                        onValueChange={(value) => setConfig({ ...config, language: value })}
                      >
                        <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="es">🇲🇽 Español</SelectItem>
                          <SelectItem value="en">🇺🇸 English</SelectItem>
                          <SelectItem value="fr">🇫🇷 Français</SelectItem>
                          <SelectItem value="it">🇮🇹 Italiano</SelectItem>
                          <SelectItem value="pt">🇧🇷 Português</SelectItem>
                          <SelectItem value="de">🇩🇪 Deutsch</SelectItem>
                          <SelectItem value="zh">🇨🇳 中文</SelectItem>
                          <SelectItem value="ko">🇰🇷 한국어</SelectItem>
                          <SelectItem value="ja">🇯🇵 日本語</SelectItem>
                          <SelectItem value="ar">🇸🇦 العربية</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label className="text-sm font-medium">Estilo de Integración</Label>
                      <div className="grid grid-cols-3 gap-3 mt-2">
                        {[
                          { value: 'modal', label: 'Modal', desc: 'Ventana emergente' },
                          { value: 'floating', label: 'Flotante', desc: 'Botón fijo' },
                          { value: 'inline', label: 'Embebido', desc: 'En la página' },
                        ].map((style) => (
                          <button
                            key={style.value}
                            onClick={() => setConfig({ ...config, buttonStyle: style.value })}
                            className={cn(
                              "p-3 rounded-xl border-2 text-center transition-all",
                              config.buttonStyle === style.value
                                ? "border-primary bg-primary/5"
                                : "border-border hover:bg-muted"
                            )}
                          >
                            <p className="font-semibold text-sm">{style.label}</p>
                            <p className="text-xs text-muted-foreground">{style.desc}</p>
                          </button>
                        ))}
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Link to occasions configuration */}
                <Card className="border-0 shadow-lg bg-gradient-to-r from-orange-50 to-amber-50 dark:from-orange-950/30 dark:to-amber-950/30">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                          <Sparkles className="h-5 w-5 text-orange-500" />
                          Configurar Ocasiones
                        </h3>
                        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                          Personaliza las ocasiones que aparecen en tu widget de reservaciones
                        </p>
                      </div>
                      <Link
                        to="/dashboard/widget-occasions"
                        className="px-4 py-2 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors"
                      >
                        Configurar
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Preview Panel */}
              <div className="space-y-4">
                <Card className="border-0 shadow-lg sticky top-20" data-testid="widget-preview-card">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Eye className="h-5 w-5 text-primary" /> Vista Previa
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex justify-center">
                      <div className="w-full max-w-[380px]">
                        <WidgetPreview />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* CODE/INTEGRATION TAB */}
          <TabsContent value="code">
            <WidgetIntegrationPanel
              type="reservation"
              restaurantId={restaurantId}
              urlParams={widgetUrlParams}
              sdkConfig={widgetSdkConfig}
              buttonStyle={config.buttonStyle}
              borderRadius={config.borderRadius || '12px'}
              productionBase={PRODUCTION_URL}
              embedTitle={config.restaurantName ? `Reservar — ${config.restaurantName}` : 'Reserva tu mesa'}
            />
          </TabsContent>


          {/* SHARE TAB */}
          <TabsContent value="share">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Share2 className="h-5 w-5 text-primary" /> Compartir en Redes Sociales
                    </CardTitle>
                    <CardDescription>Promociona tu widget de reservas</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 bg-muted rounded-xl">
                      <Label className="text-sm font-medium">Link directo al widget</Label>
                      <div className="flex gap-2 mt-2">
                        <Input 
                          value={`${PRODUCTION_URL}/widget/${restaurantId}`}
                          readOnly
                          className="rounded-xl"
                        />
                        <Button 
                          variant="outline" 
                          className="rounded-xl"
                          onClick={() => {
                            navigator.clipboard.writeText(`${PRODUCTION_URL}/widget/${restaurantId}`);
                            toast.success('Link copiado');
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <Button 
                        variant="outline" 
                        className="h-14 rounded-xl flex items-center gap-3"
                        onClick={() => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(PRODUCTION_URL + '/widget/' + restaurantId)}`, '_blank')}
                      >
                        <Facebook className="h-5 w-5 text-blue-600" />
                        <span>Facebook</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-14 rounded-xl flex items-center gap-3"
                        onClick={() => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(PRODUCTION_URL + '/widget/' + restaurantId)}&text=Reserva en nuestro restaurante`, '_blank')}
                      >
                        <Twitter className="h-5 w-5 text-sky-500" />
                        <span>Twitter/X</span>
                      </Button>
                      <Button 
                        variant="outline" 
                        className="h-14 rounded-xl flex items-center gap-3 col-span-2"
                        onClick={() => {
                          toast.info('Para Instagram, copia el link y pégalo en tu bio o historia');
                        }}
                      >
                        <Instagram className="h-5 w-5 text-pink-500" />
                        <span>Instagram (Copiar para bio)</span>
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg">Instrucciones por Red Social</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="p-4 border rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Facebook className="h-5 w-5 text-blue-600" />
                        <span className="font-medium">Facebook</span>
                      </div>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Comparte el link en tu página de negocio</li>
                        <li>Añádelo como botón "Reservar" en tu página</li>
                        <li>Inclúyelo en publicaciones promocionales</li>
                      </ul>
                    </div>
                    <div className="p-4 border rounded-xl">
                      <div className="flex items-center gap-2 mb-2">
                        <Instagram className="h-5 w-5 text-pink-500" />
                        <span className="font-medium">Instagram</span>
                      </div>
                      <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                        <li>Añade el link a tu bio</li>
                        <li>Usa el sticker de "Link" en tus historias</li>
                        <li>Menciónalo en tus publicaciones</li>
                      </ul>
                    </div>
                  </CardContent>
                </Card>
              </div>

              <div>
                <Card className="border-0 shadow-lg">
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Link2 className="h-5 w-5 text-primary" /> Código QR
                    </CardTitle>
                    <CardDescription>Imprime y coloca en tu local</CardDescription>
                  </CardHeader>
                  <CardContent className="text-center">
                    <div className="w-48 h-48 mx-auto bg-white rounded-xl p-4 mb-4">
                      <div className="w-full h-full bg-muted rounded flex items-center justify-center text-muted-foreground">
                        <span className="text-xs">QR Code</span>
                      </div>
                    </div>
                    <Button className="rounded-xl">
                      Descargar QR
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </TabsContent>

          {/* VOICE BOT TAB */}
          <TabsContent value="bot">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Bot className="h-5 w-5 text-primary" /> Bot de Voz para Reservas
                  </CardTitle>
                  <CardDescription>Asistente inteligente integrado en el widget</CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="w-16 h-16 rounded-2xl bg-primary flex items-center justify-center">
                        <Mic className="h-8 w-8 text-white" />
                      </div>
                      <div>
                        <h3 className="font-bold text-lg">Reservas por Voz</h3>
                        <p className="text-sm text-muted-foreground">Tus clientes pueden reservar hablando</p>
                      </div>
                    </div>
                    <Badge className="bg-green-500/10 text-green-600 hover:bg-green-500/20">
                      Activado
                    </Badge>
                  </div>

                  <div className="space-y-4">
                    <h4 className="font-semibold">Características</h4>
                    <div className="grid grid-cols-1 gap-3">
                      {[
                        { icon: MessageSquare, title: 'Conversación Natural', desc: 'El bot entiende lenguaje natural en español e inglés' },
                        { icon: Calendar, title: 'Extracción Automática', desc: 'Extrae fecha, hora, personas y nombre automáticamente' },
                        { icon: Clock, title: 'Respuesta Instantánea', desc: 'Responde en tiempo real con voz sintetizada' },
                        { icon: Users, title: 'Contexto Persistente', desc: 'Recuerda la conversación hasta completar la reserva' },
                      ].map((feature, i) => (
                        <div key={i} className="flex items-start gap-3 p-3 bg-muted rounded-xl">
                          <feature.icon className="h-5 w-5 text-primary mt-0.5" />
                          <div>
                            <p className="font-medium text-sm">{feature.title}</p>
                            <p className="text-xs text-muted-foreground">{feature.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Ejemplo de Conversación</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 p-4 bg-neutral-900 rounded-xl text-white text-sm">
                    <div className="flex justify-end">
                      <div className="bg-primary px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%]">
                        "Quiero reservar para 4 personas"
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-neutral-800 px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%]">
                        "¡Perfecto! 😊 ¿Para qué fecha te gustaría la reserva?"
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-primary px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%]">
                        "Mañana a las 8 de la noche"
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-neutral-800 px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%]">
                        "¡Genial! Mesa para 4 el 5 de marzo a las 20:00. ¿A nombre de quién?"
                      </div>
                    </div>
                    <div className="flex justify-end">
                      <div className="bg-primary px-4 py-2 rounded-2xl rounded-br-sm max-w-[80%]">
                        "Carlos García"
                      </div>
                    </div>
                    <div className="flex justify-start">
                      <div className="bg-neutral-800 px-4 py-2 rounded-2xl rounded-bl-sm max-w-[80%]">
                        "¡Listo Carlos! 🎉 Tu reserva está confirmada."
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 p-4 border rounded-xl">
                    <h4 className="font-semibold mb-2">El bot puede entender:</h4>
                    <div className="flex flex-wrap gap-2">
                      {['"para hoy"', '"mañana"', '"el viernes"', '"a las 8"', '"4 personas"', '"mesa para 2"'].map((phrase, i) => (
                        <Badge key={i} variant="secondary">{phrase}</Badge>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
